import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Sparkles, Quote } from 'lucide-react';
import Reveal from '../components/Reveal';
import usePageTitle from '../hooks/usePageTitle';
import { images, homeIntro, approachTools, whoWeServe, takeaways, booking } from '../mock';
import { useBooking } from '../context/BookingContext';

const Home = () => {
  usePageTitle('Home');
  const { openBooking } = useBooking();

  return (
    <main className="overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={images.heroBoardroom} alt="Elegant boardroom" className="w-full h-full object-cover animate-slowzoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#241033]/95 via-[#2C1743]/80 to-[#43265E]/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#241033]/70 via-transparent to-transparent" />
        </div>
        <div className="relative max-w-[1280px] mx-auto px-6 w-full pt-28">
          <div className="max-w-2xl">
            <div className="animate-fadeup opacity-0 eyebrow text-silver mb-6" style={{ animationDelay: '0.1s' }}>
              Strategy · Culture · Human-Centered Leadership
            </div>
            <h1 className="animate-fadeup opacity-0 font-display text-white text-5xl md:text-7xl font-medium leading-[1.05] mb-6" style={{ animationDelay: '0.25s' }}>
              Where vision meets clarity.<br />
              <span className="italic text-[#D9CEE6]">And clarity becomes action.</span>
            </h1>
            <p className="animate-fadeup opacity-0 text-[#E7E2EE] text-lg font-light leading-relaxed max-w-xl mb-10" style={{ animationDelay: '0.4s' }}>
              Executive coaching, I/O psychology consulting, and strategic partnership for purpose-driven leaders and organizations ready to move forward—on purpose.
            </p>
            <div className="animate-fadeup opacity-0 flex flex-wrap items-center gap-4" style={{ animationDelay: '0.55s' }}>
              <button onClick={() => openBooking(booking.alignmentLab, 'Book a Session')} className="btn-ivory rounded-full px-9 py-4 text-sm tracking-luxe uppercase inline-flex items-center gap-2">
                Book a Session <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <Link to="/services" className="group inline-flex items-center gap-2 text-white text-sm tracking-luxe uppercase border-b border-transparent hover:border-[#D9CEE6] pb-1 transition-all">
                Explore Services
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="eyebrow text-[#B9A9CC] text-[0.6rem]">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-[#B9A9CC] to-transparent" />
        </div>
      </section>

      {/* WELCOME / INTRO */}
      <section className="py-28 bg-[#FBFAF8]">
        <div className="max-w-[1000px] mx-auto px-6 text-center">
          <Reveal>
            <div className="eyebrow text-[#7E5C9E] mb-6">Welcome to The Answer Group Consulting, LLC</div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-4xl md:text-5xl text-[#241033] font-medium leading-tight mb-10">
              We believe clarity is essential, alignment is powerful, and strategy should serve both the mission and the people who move it forward.
            </h2>
          </Reveal>
          <div className="hairline w-24 mx-auto mb-10" />
          <div className="space-y-6 max-w-2xl mx-auto">
            {homeIntro.map((p, i) => (
              <Reveal key={p.slice(0, 32)} delay={150 + i * 100}>
                <p className="text-[#4A4453] text-lg font-light leading-relaxed">{p}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={500}>
            <p className="font-display italic text-2xl text-[#43265E] mt-10">TAG us in! We’re ready to be an answer to you.</p>
          </Reveal>
        </div>
      </section>

      {/* WHY OUR APPROACH WORKS */}
      <section className="py-28 bg-[#241033] relative">
        <div className="absolute inset-0 opacity-[0.06]">
          <img src={images.silverTexture} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1280px] mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="eyebrow text-[#7E5C9E] mb-6">Why Our Approach Works</div>
                <h2 className="font-display text-4xl md:text-5xl text-white font-medium leading-tight mb-8">
                  Our solutions are rooted in <span className="text-silver">evidence</span>, centered on the people they are meant to serve, and translated into practical systems that work.
                </h2>
                <p className="text-[#B9A9CC] font-light leading-relaxed mb-8">
                  We combine evidence-based practice, systems thinking, and people-centered design to create tools, strategies, and structures that are thoughtful, practical, and sustainable. Our approach ensures that solutions are not only well-designed, but usable and aligned with the real needs of the people and organizations they are built to support.
                </p>
                <img src={images.coaching} alt="Coaching session" className="w-full h-64 object-cover rounded-sm" />
              </Reveal>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
              {approachTools.map((tool, i) => (
                <Reveal key={tool.title} delay={i * 80}>
                  <div className="card-lift bg-[#2C1743]/60 backdrop-blur-sm border border-[#43265E]/50 rounded-sm p-7 h-full">
                    <div className="w-10 h-10 rounded-full bg-silver flex items-center justify-center mb-5">
                      <Sparkles className="w-4 h-4 text-[#241033]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-display text-xl text-white mb-2">{tool.title}</h3>
                    <p className="text-[#B9A9CC] text-sm font-light leading-relaxed">{tool.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="py-28 bg-[#FBFAF8]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-5">
                <img src={images.leadershipMeeting} alt="Leadership meeting" className="w-full h-72 object-cover rounded-sm mt-10" />
                <img src={images.collab2} alt="Team collaboration" className="w-full h-72 object-cover rounded-sm" />
              </div>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <Reveal>
                <div className="eyebrow text-[#7E5C9E] mb-6">Who We Serve</div>
                <h2 className="font-display text-4xl md:text-5xl text-[#241033] font-medium leading-tight mb-10">
                  For those ready to bring a vision into form.
                </h2>
              </Reveal>
              <ul className="space-y-5">
                {whoWeServe.map((item, i) => (
                  <Reveal key={item} delay={i * 70}>
                    <li className="flex items-start gap-4 group">
                      <span className="mt-2 w-6 h-px bg-[#43265E] group-hover:w-10 transition-all duration-500" />
                      <span className="text-[#4A4453] text-lg font-light">{item}</span>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* TAKEAWAYS */}
      <section className="py-28 bg-[#F4F1EC]">
        <div className="max-w-[1280px] mx-auto px-6">
          <Reveal>
            <div className="text-center mb-16">
              <div className="eyebrow text-[#7E5C9E] mb-6">Your Takeaways</div>
              <h2 className="font-display text-4xl md:text-5xl text-[#241033] font-medium">
                Partnership that yields momentum.
              </h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-5 sm:grid-cols-2 gap-5">
            {takeaways.map((t, i) => (
              <Reveal key={t.title} delay={i * 90}>
                <div className="card-lift bg-white rounded-sm p-8 h-full border-t-2 border-[#43265E]">
                  <div className="font-display text-5xl text-[#D9CEE6] mb-4">{String(i + 1).padStart(2, '0')}</div>
                  <h3 className="font-display text-2xl text-[#241033] mb-2">{t.title}</h3>
                  <p className="text-[#6A6570] text-sm font-light">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 bg-[#43265E] relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#5B3A7A]/40 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#2C1743]/60 blur-3xl" />
        <div className="relative max-w-[900px] mx-auto px-6 text-center">
          <Quote className="w-10 h-10 text-[#B9A9CC] mx-auto mb-8" strokeWidth={1} />
          <Reveal>
            <h2 className="font-display text-4xl md:text-6xl text-white font-medium leading-tight mb-8">
              The answer isn’t out there.<br /> It’s already within you.
            </h2>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-[#D9CEE6] font-light text-lg mb-10 max-w-xl mx-auto">
              Let’s unpack the noise, identify what truly matters, and move forward with intention—together.
            </p>
            <button onClick={() => openBooking(booking.alignmentLab, 'Book a Session')} className="btn-ivory rounded-full px-10 py-4 text-sm tracking-luxe uppercase inline-flex items-center gap-2">
              TAG Us In <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
